import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { UNITS } from "@/lib/services";

const PALETTE = ["#6366F1", "#06B6D4", "#10B981", "#F59E0B", "#F43F5E", "#A855F7", "#EC4899", "#84CC16"];
const DEFAULT_MSG = "Olá, {cliente}! Está se aproximando o período do seu próximo {servico}, previsto para {data}. Gostaria de deixar seu atendimento agendado? — {empresa}";
const blank = () => ({ name: "", interval_value: "6", interval_unit: "months", lead_days: "15", default_price: "", default_message: DEFAULT_MSG, color: PALETTE[0], active: true });

const Field = ({ label, children, htmlFor }) => <div className="space-y-1.5"><Label htmlFor={htmlFor}>{label}</Label>{children}</div>;

export const ServiceTypeDialog = ({ state, onClose, onSaved }) => {
  const editing = state?.type;
  const [f, setF] = useState(blank());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state) return;
    setF(editing ? { ...editing, interval_value: String(editing.interval_value), lead_days: String(editing.lead_days),
      default_price: editing.default_price == null ? "" : String(editing.default_price) } : blank());
  }, [state, editing]);

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const price = String(f.default_price).replace(",", ".").trim();
    const body = { name: f.name, interval_value: Number(f.interval_value), interval_unit: f.interval_unit, lead_days: Number(f.lead_days),
      default_price: price === "" ? null : Number(price), default_message: f.default_message, color: f.color, active: f.active };
    try {
      if (editing) await api.put(`/service-types/${editing.id}`, body);
      else await api.post("/service-types", body);
      toast.success(editing ? "Serviço atualizado." : "Serviço criado.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-slate-900 sm:max-w-lg" data-testid="service-type-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">{editing ? "Editar serviço" : "Novo serviço no catálogo"}</DialogTitle>
          <DialogDescription>A periodicidade define quando o próximo retorno é agendado.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nome do serviço" htmlFor="st_name"><Input id="st_name" required value={f.name} onChange={set("name")} placeholder="Ex.: Limpeza dental" className="h-11 bg-slate-950" data-testid="service-type-name-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Repetir a cada" htmlFor="st_value"><Input id="st_value" type="number" min={1} required value={f.interval_value} onChange={set("interval_value")} className="h-11 bg-slate-950" data-testid="service-type-interval-input" /></Field>
            <Field label="Unidade">
              <Select value={f.interval_unit} onValueChange={set("interval_unit")}>
                <SelectTrigger className="h-11 bg-slate-950" data-testid="service-type-unit-select"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900">
                  {Object.entries(UNITS).map(([k, [, plural]]) => <SelectItem key={k} value={k} data-testid={`service-type-unit-${k}`}>{plural}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Avisar com antecedência (dias)" htmlFor="st_lead"><Input id="st_lead" type="number" min={0} value={f.lead_days} onChange={set("lead_days")} className="h-11 bg-slate-950" data-testid="service-type-lead-input" /></Field>
            <Field label="Valor padrão (R$)" htmlFor="st_price"><Input id="st_price" inputMode="decimal" value={f.default_price} onChange={set("default_price")} placeholder="Opcional" className="h-11 bg-slate-950" data-testid="service-type-price-input" /></Field>
          </div>
          <Field label="Cor no calendário">
            <div className="flex flex-wrap gap-2">
              {PALETTE.map((c) => (
                <button key={c} type="button" onClick={() => setF({ ...f, color: c })} aria-label={`Cor ${c}`} data-testid={`service-type-color-${c.slice(1)}`}
                  className={`h-8 w-8 rounded-full transition-transform duration-150 hover:scale-110 ${f.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-slate-900" : ""}`} style={{ background: c }} />
              ))}
            </div>
          </Field>
          <Field label="Mensagem padrão" htmlFor="st_msg">
            <Textarea id="st_msg" rows={4} value={f.default_message} onChange={set("default_message")} className="bg-slate-950" data-testid="service-type-message-input" />
            <p className="text-xs text-slate-500">Variáveis: {"{cliente} {servico} {data} {valor} {empresa} {telefone}"}</p>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            {[[true, "Ativo"], [false, "Inativo"]].map(([v, label]) => (
              <button key={label} type="button" onClick={() => setF({ ...f, active: v })} data-testid={`service-type-active-${v}`}
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${f.active === v ? "border-slate-100 bg-slate-100 text-slate-900" : "border-slate-700 text-slate-300"}`}>{label}</button>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="rounded-full font-semibold" data-testid="service-type-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
